import React from 'react';
import { toPersianDigits } from '../i18n';
import { Paper, Box, Typography, Grid, Chip, useTheme } from '@mui/material';
import {
  ShowChartRounded as ShowChartRoundedIcon,
  AccessTimeRounded as AccessTimeRoundedIcon,
  AccountBalanceWalletRounded as AccountBalanceWalletRoundedIcon,
} from '@mui/icons-material';

const MarketOverview = ({ totalItems, time, isRefreshing, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        mb: 3,
        borderRadius: 2.5,
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
          : 'linear-gradient(135deg, #EFF6FF 0%, #E0E7FF 100%)',
        border: '1px solid',
        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(199, 210, 254, 0.5)',
        boxShadow: isDark
          ? '0 10px 30px -10px rgba(0,0,0,0.5)'
          : '0 10px 30px -10px rgba(99, 102, 241, 0.15)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decorative Blur Circle */}
      <Box
        sx={{
          position: 'absolute',
          top: -30,
          right: theme.direction === 'rtl' ? 'auto' : -30,
          left: theme.direction === 'rtl' ? -30 : 'auto',
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, rgba(0,0,0,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#DBEAFE',
                color: theme.palette.primary.main,
              }}
            >
              <ShowChartRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {t('liveMarketRates')}
            </Typography>
            <Chip
              icon={<span className="live-pulse-dot" style={{ display: 'block', margin: 0 }} />}
              label={isRefreshing ? t('updating') : t('live')}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 24,
                px: 1,
                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#D1FAE5',
                color: '#10B981',
                border: '1px solid',
                borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : '#A7F3D0',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiChip-icon': {
                  margin: '0 !important',
                  display: 'inline-flex !important',
                  alignItems: 'center !important',
                  justifyContent: 'center !important',
                },
                '& .MuiChip-label': {
                  padding: '0 !important',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                },
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('liveMarketDesc')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              flexWrap: 'wrap',
              pt: { xs: 1, md: 0 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                px: 2,
                borderRadius: 3,
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexGrow: { xs: 1, sm: 0 },
              }}
            >
              <AccountBalanceWalletRoundedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('totalAssets')}
                </Typography>
                <Typography variant="subtitle2" className="mono-num" sx={{ fontWeight: 700 }}>
                  {theme.direction === 'rtl' ? toPersianDigits(totalItems || 0) : (totalItems || 0)} {t('quotes')}
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                px: 2,
                borderRadius: 3,
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexGrow: { xs: 1, sm: 0 },
              }}
            >
              <AccessTimeRoundedIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('lastUpdated')}
                </Typography>
                <Typography variant="subtitle2" className="mono-num" sx={{ fontWeight: 700 }}>
                  {time || t('loading')}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MarketOverview;
